import { router } from '@inertiajs/react'
import { useState } from 'react'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatScheduledAt(iso) {
  if (!iso) return '—'
  const date = new Date(iso)
  const now  = new Date()
  const isToday    = date.toDateString() === now.toDateString()
  const tomorrow   = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const isTomorrow = date.toDateString() === tomorrow.toDateString()
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  if (isToday)    return `Today ${time}`
  if (isTomorrow) return `Tomorrow ${time}`
  const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${dateStr} · ${time}`
}

function timeUntil(iso) {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return 'Now'
  const totalMin = Math.round(ms / 60_000)
  if (totalMin < 60) return `In ${totalMin} min`
  const hours = Math.floor(totalMin / 60)
  const mins  = totalMin % 60
  return mins > 0 ? `In ${hours}h ${mins}m` : `In ${hours}h`
}

const STATUS_STYLE = {
  pending:    { label: 'Scheduled', color: '#2196F3' },
  activating: { label: 'Starting',  color: '#FF9800' },
  active:     { label: 'Active',    color: '#4CAF50' },
  cancelled:  { label: 'Cancelled', color: '#F44336' },
  completed:  { label: 'Complete',  color: '#607D8B' },
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { label: status, color: '#9E9E9E' }
  return (
    <span
      style={{ backgroundColor: s.color + '22', color: s.color, borderColor: s.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold"
    >
      <span style={{ backgroundColor: s.color }} className="w-2 h-2 rounded-full" />
      {s.label}
    </span>
  )
}

// ─── Session row ──────────────────────────────────────────────────────────────

function ScheduledSessionRow({ session, onCancel }) {
  const isPending  = session.status === 'pending'
  const isActive   = session.status === 'active'
  const until      = timeUntil(session.scheduled_at)

  return (
    <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{session.offer_name}</p>
        {session.venue_name && (
          <p className="text-xs text-gray-500 truncate">{session.venue_name}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-600">{formatScheduledAt(session.scheduled_at)}</span>
          {until && <span className="text-xs text-blue-600 font-medium">{until}</span>}
          {session.position_hint && (
            <span className="text-xs text-gray-400">· est. position {session.position_hint}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
        <StatusBadge status={session.status} />
        {isActive && session.queue_session_id && (
          <a
            href={`/queues/${session.queue_session_id}`}
            className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            View Queue
          </a>
        )}
        {isPending && (
          <button
            onClick={() => onCancel(session.id)}
            className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Property session card ────────────────────────────────────────────────────

function PropertySessionCard({ propertySession, onCancel }) {
  const hasScheduled = propertySession.scheduled_sessions.length > 0

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 mb-4 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-gray-800">
            Stay · Session #{propertySession.id}
          </p>
          {propertySession.activated_at && (
            <p className="text-xs text-gray-500">
              Checked in {formatScheduledAt(propertySession.activated_at)}
              {propertySession.scheduled_checkout_at &&
                ` · Out ${formatScheduledAt(propertySession.scheduled_checkout_at)}`}
            </p>
          )}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          propertySession.session_status === 'active'
            ? 'bg-green-100 text-green-700'
            : 'bg-yellow-100 text-yellow-700'
        }`}>
          {propertySession.session_status}
        </span>
      </div>

      {hasScheduled ? (
        propertySession.scheduled_sessions.map(s => (
          <ScheduledSessionRow key={s.id} session={s} onCancel={onCancel} />
        ))
      ) : (
        <p className="px-4 py-3 text-sm text-gray-400 italic">No scheduled appointments.</p>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MySchedule({ property_sessions }) {
  const [cancelling, setCancelling] = useState(null)

  function handleCancel(scheduledSessionId) {
    if (!confirm('Cancel this scheduled session?')) return
    setCancelling(scheduledSessionId)
    router.delete(`/api/v1/unified_api/scheduled_sessions/${scheduledSessionId}`, {
      onFinish: () => {
        setCancelling(null)
        router.reload({ only: ['property_sessions'] })
      },
    })
  }

  const hasAny = property_sessions.some(ps => ps.scheduled_sessions.length > 0)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Schedule</h1>
          <p className="text-sm text-gray-500 mt-1">Your upcoming appointments across active stays and events.</p>
        </div>

        {/* Sessions */}
        {property_sessions.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
            <p className="text-gray-400 text-sm">No active stays or upcoming appointments.</p>
          </div>
        ) : !hasAny ? (
          property_sessions.map(ps => (
            <PropertySessionCard key={ps.id} propertySession={ps} onCancel={handleCancel} />
          ))
        ) : (
          property_sessions.map(ps => (
            <PropertySessionCard key={ps.id} propertySession={ps} onCancel={handleCancel} />
          ))
        )}
      </div>
    </div>
  )
}
